/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import GoogleMapReact, { ChangeEventValue } from 'google-map-react'
import React from 'react'
import { ReactFC } from '@frontend/utils/react-compat'
import {
  FacilityData,
  SerializedResponseData,
} from '../../entites/FacilitySerializedForMap'
import { ApiClient } from '../../utils/ApiClient'
import { getOpeningTimeText } from '../../utils/getOpeningTimeText'
import { RemoveModal } from './BookmarkModal'
import { InfoButton } from './InfoButton'
import { InfoModal } from './InfoModal'
import { MapAlert } from './MapAlert'
import { MapCurrentLocationButton } from './MapCurrentLocationButton'
import { MapFacilityInfo } from './MapFacilityInfo'
import { MapMarker } from './MapMarker'
import { MapSwitchDate } from './MapSwitchDate'

type GeolocationPosition = {
  lat: number
  lng: number
}

const tokyoStationPosition = { lat: 35.681236, lng: 139.767125 }

export type MapProps = {
  apiKey: string
}

export const Map = ({ apiKey }: MapProps) => {
  const [alertMessage, setAlertMessage] = React.useState<string>('')
  const [markedFacilities, setMarkedFacilities] = React.useState<
    FacilityData[]
  >([])
  const [map, setMap] = React.useState<google.maps.Map | null>(null)
  const [isSignedIn, setIsSignedIn] = React.useState<boolean>(false)
  const [isMoving, setIsMoving] = React.useState<boolean>(false)
  const [searchToday, setSearchToday] = React.useState<boolean>(
    new Date().getHours() < 12,
  )
  const [removeModalOpen, setRemoveModalOpen] = React.useState<boolean>(false)
  const [infoModalOpen, setInfoModalOpen] = React.useState<boolean>(false)

  const urlParams = new URLSearchParams(window.location.search)
  const dateParam = urlParams.get('date')
  const infoWindowRef = React.useRef<HTMLDivElement>(null)
  const [defaultCenter, setDefaultCenter] =
    React.useState<GeolocationPosition | null>(null)

  React.useEffect(() => {
    if (dateParam === 'tommorow') {
      setSearchToday(false)
    }
  }, [dateParam])

  React.useEffect(() => {
    setOpenedFacility(null)
  }, [searchToday])

  const [openedFacility, setOpenedFacility] =
    React.useState<FacilityData | null>(null)

  const getUserHomePosition = (): GeolocationPosition | null => {
    // @ts-ignore
    const latitude = document.getElementById('latitude').value
    // @ts-ignore
    const longitude = document.getElementById('longitude').value
    if (latitude !== '' || longitude !== '')
      return { lat: parseFloat(latitude), lng: parseFloat(longitude) }
    return null
  }

  // 参考：https://teratail.com/questions/131373
  const getUserCurrentPositionAsync =
    async (): Promise<GeolocationPosition | null> => {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords
            const p: GeolocationPosition = {
              lat: latitude,
              lng: longitude,
            }
            resolve(p)
          },
          (error) => {
            reject(error)
          },
          { timeout: 3000 },
        )
      })
    }

  const handleClickMarker = (facility: FacilityData) => () => {
    setIsMoving(true)
    setOpenedFacility(facility)
  }

  React.useEffect(() => {
    if (!openedFacility) return
    if (!map) return
    map.panTo({
      lat: openedFacility.attributes.latitude,
      lng: openedFacility.attributes.longitude,
    })

    // 中心をずらして、info window と marker の位置を調整
    let infoWindowHeight = 340
    if (infoWindowRef.current) {
      infoWindowHeight = infoWindowRef.current.clientHeight
    }

    // ずらす距離を調整
    const panPixel =
      infoWindowHeight < 270
        ? -1 * (infoWindowHeight * 0.45)
        : -1 * (infoWindowHeight * 0.4)
    map.panBy(0, panPixel)

    // 移動のアニメーションがあるため、一定時間後にフラグを変更
    setTimeout(() => {
      setIsMoving(false)
    }, 800)
  }, [openedFacility])

  const handleChange = (v: ChangeEventValue) => {
    !isMoving && setOpenedFacility(null)
    setMarkers({
      neLat: v.bounds.ne.lat,
      neLng: v.bounds.ne.lng,
      swLat: v.bounds.sw.lat,
      swLng: v.bounds.sw.lng,
    })
  }

  const handleClick = (e) => {
    if (!!openedFacility && !e.event.target.closest('.marker')) {
      setOpenedFacility(null)
    }
  }

  type BoundsParams = {
    neLat: number
    neLng: number
    swLat: number
    swLng: number
  }

  const setMarkers = (params: BoundsParams) => {
    const searchParams = new URLSearchParams()

    searchParams.set('ne_lat', params.neLat.toString())
    searchParams.set('ne_lng', params.neLng.toString())
    searchParams.set('sw_lat', params.swLat.toString())
    searchParams.set('sw_lng', params.swLng.toString())

    if (
      document.getElementById('hidden_category_sick_child') &&
      // @ts-ignore
      document.getElementById('hidden_category_sick_child').value === 'true'
    ) {
      searchParams.set('category_sick_child', 'true')
    }
    if (
      document.getElementById('hidden_pre_registration_type_1') &&
      // @ts-ignore
      document.getElementById('hidden_pre_registration_type_1').value === 'true'
    ) {
      searchParams.set('hidden_pre_registration_type_1', 'true')
    }
    if (
      document.getElementById('hidden_pre_registration_type_2') &&
      // @ts-ignore
      document.getElementById('hidden_pre_registration_type_2').value === 'true'
    ) {
      searchParams.set('hidden_pre_registration_type_2', 'true')
    }
    if (
      document.getElementById('hidden_pre_registration_type_3') &&
      // @ts-ignore
      document.getElementById('hidden_pre_registration_type_3').value === 'true'
    ) {
      searchParams.set('hidden_pre_registration_type_3', 'true')
    }
    // @ts-ignore
    if (document.getElementById('latitude').value !== null) {
      // @ts-ignore
      searchParams.set('latitude', document.getElementById('latitude').value)
    }
    // @ts-ignore
    if (document.getElementById('longitude').value !== null) {
      // @ts-ignore
      searchParams.set('longitude', document.getElementById('longitude').value)
    }

    fetch('/map_facilities/facilities?' + searchParams.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
        return res.json()
      })
      .then((data: SerializedResponseData) => {
        if (data.data.length > 100) {
          setAlertMessage('地図を拡大してください')
          return
        } else {
          setAlertMessage('')
        }

        // 無限ループを防ぐために、フェッチしたデータに変更があった場合のみマーカーをセット
        const isSame =
          data.data.length === markedFacilities.length &&
          data.data.every((facility) =>
            markedFacilities.some((f) => f.id === facility.id),
          )
        if (isSame) return
        setIsSignedIn(data.meta.is_signed_in)
        setMarkedFacilities(data.data)
      })
  }

  const handleSwitchDate = (searchToday: boolean) => {
    const href = searchToday
      ? '/map_facilities'
      : '/map_facilities?date=tommorow'
    window.history.pushState(null, '', href)
    setSearchToday(searchToday)
  }

  const handleBookmark = async () => {
    if (!openedFacility) return
    const url = `/facilities/${openedFacility.id}/family_facility_bookmarks`
    try {
      const res = await ApiClient.post(url)
      if (res.ok) {
        const data = await res.json()
        setOpenedFacility({
          ...openedFacility,
          attributes: {
            ...openedFacility.attributes,
            bookmark_id: data.id,
          },
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleUnbookmark = async (removeModal?: boolean) => {
    if (!openedFacility) return
    let url = `/facilities/${openedFacility.id}/family_facility_bookmarks/${openedFacility.attributes.bookmark_id}`
    if (removeModal) {
      url = url + '?remove_bookmark_modal=true'
    }
    try {
      const res = await ApiClient.delete(url)
      // 404 はすでに削除済みなので削除成功として扱う
      if (res.ok || res.status === 404) {
        setOpenedFacility({
          ...openedFacility,
          attributes: {
            ...openedFacility.attributes,
            bookmark_id: null,
          },
        })
      } else {
        // DTL-17912 参照
        const json = await res.json()
        if (json.message) {
          window.alert(json.message)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onClickCurrentLocationButton = async () => {
    if (!map) return
    if (!navigator.geolocation) return
    try {
      // 位置情報取得成功 => 現在地
      const currentPosition = await getUserCurrentPositionAsync()
      if (currentPosition) return map.setCenter(currentPosition)
    } catch (error) {
      // 取得失敗 => アラート
      switch (error.code) {
        case 1: //PERMISSION_DENIED
          alert('位置情報の利用が許可されていません')
          break
        case 2: //POSITION_UNAVAILABLE
          alert('現在位置が取得できませんでした')
          break
        case 3: //TIMEOUT
          alert('タイムアウトになりました')
          break
        default:
          alert('エラーコード：' + error.code)
          break
      }
    }
  }

  React.useLayoutEffect(() => {
    const userHomePosition = getUserHomePosition()
    if (userHomePosition) {
      // ユーザーがログインしている
      setDefaultCenter(userHomePosition)
    } else if (navigator.geolocation) {
      // ユーザー非ログイン・位置情報オン
      navigator.geolocation.getCurrentPosition(
        (p) => {
          setDefaultCenter({
            lat: p.coords.latitude,
            lng: p.coords.longitude,
          })
        },
        (err) => {
          console.error(err)
          setDefaultCenter(tokyoStationPosition)
        },
        { timeout: 3000 },
      )
    }
  }, [])

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        margin: 'auto',
        position: 'relative',
      }}
    >
      <SwitchDateButtonContainer>
        <MapSwitchDate today={searchToday} handleSwitch={handleSwitchDate} />
      </SwitchDateButtonContainer>
      <CurrentLocationButtonContainer>
        <MapCurrentLocationButton onClick={onClickCurrentLocationButton} />
      </CurrentLocationButtonContainer>
      <InfoButtonContainer>
        <InfoButton
          onClick={() => {
            if (openedFacility) setOpenedFacility(null)
            setInfoModalOpen(true)
          }}
        />
      </InfoButtonContainer>
      {defaultCenter && (
        <GoogleMapReact
          bootstrapURLKeys={{ key: apiKey }}
          defaultCenter={defaultCenter}
          defaultZoom={14}
          onChange={handleChange}
          onClick={handleClick}
          options={{
            streetViewControl: false,
            fullscreenControl: false,
            zoomControl: false,
          }}
          onGoogleApiLoaded={(maps) => {
            setMap(maps.map)
          }}
          yesIWantToUseGoogleMapApiInternals
        >
          {markedFacilities.map((facility) => (
            <MapMarker
              key={facility.id}
              lat={facility.attributes.latitude}
              lng={facility.attributes.longitude}
              text={facility.attributes.name}
              statusCode={
                searchToday
                  ? facility.attributes.today_available_status
                  : facility.attributes.tomorrow_available_status_code
              }
              onClick={handleClickMarker(facility)}
              selected={!!openedFacility && facility.id === openedFacility.id}
            />
          ))}
        </GoogleMapReact>
      )}
      <div
        css={css`
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translateY(-79%) translateX(-50%);
          z-index: 6;
          width: calc(100% - 28px);
        `}
      >
        {/* 以下は参考にしたページ */}
        {/* https://github.com/google-map-react/google-map-react-examples/blob/master/src/examples/MarkerInfoWindowGmapsObj.js */}

        {openedFacility && (
          <div ref={infoWindowRef}>
            <MapFacilityInfo
              id={openedFacility.id}
              name={openedFacility.attributes.name}
              phoneNumber={openedFacility.attributes.phone_number}
              contractStatus={openedFacility.attributes.contract_status_code}
              imageUrl={openedFacility.attributes.primary_image_url}
              siteUrl={openedFacility.attributes.detail_url_or_site_url}
              onClose={() => {
                setOpenedFacility(null)
              }}
              todayStatusCode={openedFacility.attributes.today_available_status}
              todayStatusText={
                openedFacility.attributes.today_available_status_text
              }
              tomorrowStatusCode={
                openedFacility.attributes.tomorrow_available_status_code
              }
              tomorrowStatusText={
                openedFacility.attributes.tomorrow_available_status_text
              }
              distance={openedFacility.attributes.distance}
              googleMapUrl={openedFacility.attributes.google_map_url}
              latitude={openedFacility.attributes.latitude}
              longitude={openedFacility.attributes.longitude}
              bookmarkId={openedFacility.attributes.bookmark_id}
              bookmarkModalShowEnabled={
                openedFacility.attributes.bookmark_modal_show_enabled
              }
              openingTimes={
                openedFacility.attributes.facility_opening_times
                  ? openedFacility.attributes.facility_opening_times.map((t) =>
                      getOpeningTimeText(t, true),
                    )
                  : []
              }
              usageBusinessDayText={
                openedFacility.attributes.usage_business_day_text
              }
              isSignedIn={isSignedIn}
              handleBookmark={handleBookmark}
              handleUnbookmark={
                openedFacility.attributes.bookmark_modal_show_enabled
                  ? () => {
                      setRemoveModalOpen(true)
                    }
                  : handleUnbookmark
              }
              acceptanceTimeText={
                openedFacility.attributes.acceptance_time_text
              }
              acceptanceStatus={openedFacility.attributes.acceptance_status}
              acceptancePhoneTimeText={
                openedFacility.attributes.acceptance_phone_time_text
              }
              acceptancePhoneStatus={
                openedFacility.attributes.acceptance_phone_status
              }
              searchToday={searchToday}
              showPath={openedFacility.attributes.facility_show_path}
              isVisualizationGroupFacility={
                openedFacility.attributes.is_visualization_group_facility
              }
              useRegistrationEnable={
                openedFacility.attributes.use_registration_enable
              }
              useReservationEnable={
                openedFacility.attributes.use_reservation_enable
              }
              useReservationStartDate={
                openedFacility.attributes.use_reservation_start_date
              }
              attachedClinicName={
                openedFacility.attributes.attached_clinic
                  ? openedFacility.attributes.attached_clinic.name
                  : null
              }
              nextWorkDayAcceptancePhoneTimeText={
                openedFacility.attributes
                  .next_work_day_acceptance_phone_time_text
              }
              detailPublished={openedFacility.attributes.detail_published}
            />
          </div>
        )}
      </div>
      <MapAlert message={alertMessage} />
      <RemoveModal
        open={removeModalOpen}
        handleClose={() => setRemoveModalOpen(false)}
        handleUnbookmark={handleUnbookmark}
      />
      <InfoModal
        open={infoModalOpen}
        handleClose={() => setInfoModalOpen(false)}
      />
    </div>
  )
}

const SwitchDateButtonContainer: ReactFC = ({ children }) => {
  return (
    <div
      css={css`
        display: flex;
        justify-content: center;
        width: 100%;
        position: absolute;
        top: 21px;
        z-index: 5;
      `}
    >
      {children}
    </div>
  )
}
const CurrentLocationButtonContainer: ReactFC = ({ children }) => {
  return (
    <div
      css={css`
        text-align: right;
        position: absolute;
        bottom: 93px;
        left: 10px;
        right: 10px;
        z-index: 8;
        max-width: 768px;
        margin-right: auto;
        margin-left: auto;
      `}
    >
      {children}
    </div>
  )
}
const InfoButtonContainer: ReactFC = ({ children }) => {
  return (
    <div
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
        width: 24px;
        height: 34px;
        position: absolute;
        top: 21px;
        right: 12px;
        z-index: 6;
        margin-right: auto;
        margin-left: auto;
      `}
    >
      {children}
    </div>
  )
}
